export type HeatmapScale = {
  lowerIsBetter: boolean;
  lowerThreshold: number;
  upperThreshold: number;
};

export type HeatmapMetric<Row> = {
  key: string;
  lowerIsBetter?: boolean;
  getNumericValue: (row: Row) => number | undefined;
};

export type HeatmapTier = "green" | "yellow" | "red" | null;

// Future reference: flip this back to true when we want player profile stat heatmap colors enabled again.
export const PLAYER_PROFILE_STAT_COLOR_CODING_ENABLED = false;

export function buildHeatmapScales<Row>(
  rows: Row[],
  metrics: HeatmapMetric<Row>[]
): Record<string, HeatmapScale | null> {
  const scales: Record<string, HeatmapScale | null> = {};

  metrics.forEach((metric) => {
    const values = rows
      .map((row) => metric.getNumericValue(row))
      .filter((value): value is number => value != null && Number.isFinite(value))
      .sort((a, b) => a - b);

    if (!values.length || values[0] === values[values.length - 1]) {
      scales[metric.key] = null;
      return;
    }

    const lowerIndex = Math.floor((values.length - 1) * 0.35);
    const upperIndex = Math.floor((values.length - 1) * 0.8);
    const lowerThreshold = values[Math.max(0, lowerIndex)];
    const upperThreshold = values[Math.max(0, upperIndex)];

    if (lowerThreshold === upperThreshold) {
      scales[metric.key] = null;
      return;
    }

    scales[metric.key] = {
      lowerIsBetter: metric.lowerIsBetter === true,
      lowerThreshold,
      upperThreshold,
    };
  });

  return scales;
}

export function getHeatmapTier(
  scale: HeatmapScale | null | undefined,
  value: number | undefined
): HeatmapTier {
  if (!scale || value == null || !Number.isFinite(value)) return null;

  if (scale.lowerIsBetter) {
    if (value <= scale.lowerThreshold) return "green";
    if (value < scale.upperThreshold) return "yellow";
    return "red";
  }

  if (value >= scale.upperThreshold) return "green";
  if (value > scale.lowerThreshold) return "yellow";
  return "red";
}

export function getHeatmapCellStyle(tier: HeatmapTier) {
  if (tier === "green") {
    return {
      background: "#2d4a2f",
      color: "#ddf7d7",
    };
  }

  if (tier === "yellow") {
    return {
      background: "#56481f",
      color: "#ffe9ac",
    };
  }

  if (tier === "red") {
    return {
      background: "#5a2a2d",
      color: "#ffd5d5",
    };
  }

  return {
    background: "#2c2c32",
    color: "#fff",
  };
}
